import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { fetchTitilerPointValue } from '../utils/titilerPoint';
import { alertTagsForType, userReceivesAlert } from '../../../config/audience';

export type PestRiskPestKey =
  | 'bacterial_leaf_blight'
  | 'bacterial_leaf_streak'
  | 'brown_planthopper'
  | 'green_leafhopper'
  | 'leaffolder'
  | 'rice_black_bug'
  | 'rice_blast'
  | 'rice_bug_complex'
  | 'rodents'
  | 'stem_borer';

export type PestRiskQuarterKey = 'q1' | 'q2' | 'q3' | 'q4';

export interface PestRiskPest {
  key: PestRiskPestKey;
  label: string;
}

export interface PestRiskFrame {
  quarter: PestRiskQuarterKey;
  /** Slider id, e.g. `q1` */
  date: string;
  /** Human label, e.g. `Q1 (Jan–Mar)` */
  label: string;
  cogUrl: string;
}

/** S3 COG URLs — one file per pest per calendar quarter. */
export const PEST_RISK_COG_URLS: Record<PestRiskPestKey, Partial<Record<PestRiskQuarterKey, string>>> = {
  bacterial_leaf_blight: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_blight/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_blight/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_blight/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_blight/q4.tif',
  },
  bacterial_leaf_streak: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_streak/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_streak/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_streak/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/bacterial_leaf_streak/q4.tif',
  },
  brown_planthopper: {
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/brown_planthopper/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/brown_planthopper/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/brown_planthopper/q4.tif',
  },
  green_leafhopper: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/green_leafhopper/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/green_leafhopper/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/green_leafhopper/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/green_leafhopper/q4.tif',
  },
  leaffolder: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/leaffolder/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/leaffolder/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/leaffolder/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/leaffolder/q4.tif',
  },
  rice_black_bug: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_black_bug/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_black_bug/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_black_bug/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_black_bug/q4.tif',
  },
  rice_blast: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_blast/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_blast/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_blast/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_blast/q4.tif',
  },
  rice_bug_complex: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_bug_complex/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_bug_complex/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_bug_complex/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rice_bug_complex/q4.tif',
  },
  rodents: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rodents/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rodents/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rodents/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/rodents/q4.tif',
  },
  stem_borer: {
    q1: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/stem_borer/q1.tif',
    q2: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/stem_borer/q2.tif',
    q3: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/stem_borer/q3.tif',
    q4: 'https://digisaka.s3.amazonaws.com/pest_risk_maps/stem_borer/q4.tif',
  },
};

export const PEST_RISK_PESTS: PestRiskPest[] = [
  { key: 'bacterial_leaf_blight', label: 'Bacterial Leaf Blight' },
  { key: 'bacterial_leaf_streak', label: 'Bacterial Leaf Streak' },
  { key: 'brown_planthopper', label: 'Brown Planthopper' },
  { key: 'green_leafhopper', label: 'Green Leafhopper' },
  { key: 'leaffolder', label: 'Leaffolder' },
  { key: 'rice_black_bug', label: 'Rice Black Bug' },
  { key: 'rice_blast', label: 'Rice Blast' },
  { key: 'rice_bug_complex', label: 'Rice Bug Complex' },
  { key: 'rodents', label: 'Rodents' },
  { key: 'stem_borer', label: 'Stem Borer' },
];

export const DEFAULT_PEST_RISK_PEST_KEY: PestRiskPestKey = 'bacterial_leaf_blight';

const QUARTER_META: Record<PestRiskQuarterKey, { label: string; months: string }> = {
  q1: { label: 'Q1', months: 'Jan–Mar' },
  q2: { label: 'Q2', months: 'Apr–Jun' },
  q3: { label: 'Q3', months: 'Jul–Sep' },
  q4: { label: 'Q4', months: 'Oct–Dec' },
};

const QUARTER_ORDER: PestRiskQuarterKey[] = ['q1', 'q2', 'q3', 'q4'];

export function getPestRiskFrames(pestKey: PestRiskPestKey): PestRiskFrame[] {
  const urls = PEST_RISK_COG_URLS[pestKey] ?? {};
  return QUARTER_ORDER.flatMap((quarter) => {
    const cogUrl = urls[quarter];
    if (!cogUrl) return [];
    const meta = QUARTER_META[quarter];
    return [{
      quarter,
      date: quarter,
      label: `${meta.label} (${meta.months})`,
      cogUrl,
    }];
  });
}

export function getPestRiskPestLabel(pestKey: PestRiskPestKey): string {
  return PEST_RISK_PESTS.find((p) => p.key === pestKey)?.label ?? pestKey;
}

/** Philippines extent (EPSG:4326) — same national footprint as CADI / planting method maps. */
export const PEST_RISK_BBOX: [number, number, number, number] = [
  116.92547708138447, 4.585505709624794, 126.60452291861552, 21.124494290375203,
];

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** Normalized Pest Occurrence Probability Index 0–1 (higher = greater occurrence probability). */
export const PEST_RISK_RESCALE: [number, number] = [0, 1];

/**
 * Yellow–orange–red ramp: low → high occurrence probability.
 * TiTiler `ylorrd` matches matplotlib Yellow-Orange-Red.
 */
export const PEST_RISK_COLORMAP = 'ylorrd' as const;

export const PEST_OCCURRENCE_PROBABILITY_INDEX_NAME =
  'Pest Occurrence Probability Index';

export const PEST_RISK_DESCRIPTION =
  'Nationwide rice Pest Occurrence Probability Index maps derived from seasonal climate suitability models. Each quarter shows typical seasonal occurrence probability for that part of the year.';

export const PEST_RISK_LEGEND_DESCRIPTION =
  'Pest Occurrence Probability Index ranges from 0 (very low) to 1 (very high). Yellow indicates lower occurrence probability, orange moderate, and red higher probability of pest or disease occurrence.';

/**
 * Alert-tab shortlist (P0). Full map catalogue stays in PEST_RISK_PESTS;
 * Alerts only surface these high-impact pests to avoid noise.
 */
export const PEST_ALERT_KEYS: PestRiskPestKey[] = [
  'rice_blast',
  'bacterial_leaf_blight',
  'brown_planthopper',
  'stem_borer',
];

export function pestAlertKeysForUser(
  user?: { role?: string; sub_role?: string | null } | null
): PestRiskPestKey[] {
  return PEST_ALERT_KEYS.filter((key) => userReceivesAlert(user ?? null, alertTagsForType(key)));
}

export function getCurrentPestQuarterKey(now = new Date()): PestRiskQuarterKey {
  const q = (Math.floor(now.getMonth() / 3) + 1) as 1 | 2 | 3 | 4;
  return `q${q}` as PestRiskQuarterKey;
}

/** Alert frames for current calendar quarter only (alert pest shortlist). */
export function getPestAlertFrames(
  now = new Date(),
  user?: { role?: string; sub_role?: string | null } | null
): Array<{ pestKey: PestRiskPestKey; pestLabel: string; frame: PestRiskFrame }> {
  const quarter = getCurrentPestQuarterKey(now);
  const keys = user ? pestAlertKeysForUser(user) : PEST_ALERT_KEYS;
  return keys.flatMap((pestKey) => {
    const frames = getPestRiskFrames(pestKey);
    const frame = frames.find((f) => f.quarter === quarter);
    if (!frame) return [];
    return [{ pestKey, pestLabel: getPestRiskPestLabel(pestKey), frame }];
  });
}

/** Keep only points for the current calendar quarter. */
export function filterPestPointsForAlertsTab<
  T extends { quarter: string },
>(points: T[], now = new Date()): T[] {
  const quarter = getCurrentPestQuarterKey(now);
  return points.filter((p) => p.quarter === quarter);
}

/** Highest-risk current-quarter point (for the primary Alerts card). */
export function pickTopPestAlertPoint<
  T extends { quarter: string; mean: number },
>(points: T[], now = new Date()): T | null {
  const current = filterPestPointsForAlertsTab(points, now).filter(
    (p) => Number.isFinite(p.mean)
  );
  if (current.length === 0) return null;
  return current.reduce((best, p) => (p.mean > best.mean ? p : best));
}

export const PEST_RISK_LEGEND: {
  colors: string[];
  labels: string[];
  description: string;
  legendDescription: string;
  min: number;
  max: number;
} = {
  colors: ['#ffffcc', '#fed976', '#fd8d3c', '#f03b20', '#800026'],
  labels: ['Very low', 'Low', 'Moderate', 'High', 'Very high'],
  description: 'Pest Occurrence Probability Index — yellow (low) to red (high), 0–1',
  legendDescription: PEST_RISK_LEGEND_DESCRIPTION,
  min: PEST_RISK_RESCALE[0],
  max: PEST_RISK_RESCALE[1],
};

export function pestCategoryFromValue(value: number): { label: string; color: string } {
  const steps = [
    { max: 0.2, label: PEST_RISK_LEGEND.labels[0], color: PEST_RISK_LEGEND.colors[0] },
    { max: 0.4, label: PEST_RISK_LEGEND.labels[1], color: PEST_RISK_LEGEND.colors[1] },
    { max: 0.6, label: PEST_RISK_LEGEND.labels[2], color: PEST_RISK_LEGEND.colors[2] },
    { max: 0.8, label: PEST_RISK_LEGEND.labels[3], color: PEST_RISK_LEGEND.colors[3] },
  ];
  for (const step of steps) {
    if (value < step.max) return { label: step.label, color: step.color };
  }
  return {
    label: PEST_RISK_LEGEND.labels[4],
    color: PEST_RISK_LEGEND.colors[4],
  };
}

export async function fetchPestPointValue(
  cogUrl: string,
  lon: number,
  lat: number,
  signal?: AbortSignal
): Promise<{ value: number | null; label: string; color: string }> {
  const raw = await fetchTitilerPointValue(cogUrl, lon, lat, { nodata: 'nan', signal });
  const value = raw != null && raw >= 0 && raw <= 1 ? raw : null;
  const category = value == null
    ? { label: 'No data', color: '#9ca3af' }
    : pestCategoryFromValue(value);
  return { value, label: category.label, color: category.color };
}

export function buildPestRiskTileUrl(cogUrl: string): string {
  const [min, max] = PEST_RISK_RESCALE;
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', '1');
  params.set('rescale', `${min},${max}`);
  params.set('colormap_name', PEST_RISK_COLORMAP);
  params.set('resampling', 'nearest');
  params.set('nodata', 'nan');
  params.set('format', 'png');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}
